import { CSSProperties, useContext, useEffect, useRef, useState } from 'react';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';
import DevicesIcon from '@mui/icons-material/Devices';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import gsap from 'gsap';
import { useAppSelector } from 'hooks';
import { getAccessToken } from 'utils/auth';
import { currencyFormatter } from 'utils/functions';
import { SOCKET_SERVER_URL } from 'utils/urls';

import { CashierContext, PaymentMethod } from './CashierContext';
import { parseCurrencyAmount, sanitizeCurrencyAmount } from './currencyInput';
import { checkAddressRequired } from './useAddressCheck';
import { useCashierLimits } from './useCashierLimits';
import { useRiskAssessment } from './useRiskAssessment';
import { TMXPageData } from 'utils/threatmetrix';
import { getBackofficeUrl } from 'utils/urls';
import { errorToLogObject, pickApiErrorMessage, safeReadJson } from 'utils/apiErrors';
import {
  ADDRESS_REQUIRED_TRANSACTION_MESSAGE,
  CLUB_NOT_SELECTED,
  CONNECTION_ERROR_MESSAGE,
  CRYPTO_MINIMUM_MESSAGE,
  ERROR_MESSAGE,
  EXCEEDED_MAXIMUM_WITHDRAW,
  GO_TO_ACCOUNT_SETTINGS_BUTTON,
  IDENTITY_CHECK_FAILED_MESSAGE,
  IDENTITY_PROCESS_CHECKING,
  NO_VERIFICATION_MESSAGE,
  WITHDRAWAL_WALLET_ADDRESS_EMPTY_MESSAGE1,
  WITHDRAWAL_WALLET_ADDRESS_EMPTY_MESSAGE2,
  WITHDRAW_MINIMUM,
  formatUnityString
} from 'utils/unityStrings';

function getDescopeToken(): string {
  return getAccessToken();
}

import { usePlayerProfile } from 'components/Profile/usePlayerProfile';

import KYCModal from './KYCModal';
import MissingCryptoWalletModal from './MissingCryptoWalletModal';
import MFAView from './MFAView';
import TierExplainer from './TierExplainer';
import { useClubs } from './useClubs';

const DIGITAL_METHODS: {
  key: PaymentMethod;
  label: string;
  icon: React.ReactNode;
  desc: string;
}[] = [
  {
    key: 'crypto',
    label: 'Bitcoin (BTC)',
    icon: <CurrencyBitcoinIcon style={{ fontSize: '1.2rem' }} />,
    desc: 'Withdraw to your BTC wallet'
  },
  {
    key: 'p2p',
    label: 'Player Transfer',
    icon: <SwapHorizIcon style={{ fontSize: '1.2rem' }} />,
    desc: 'Shares the online withdrawal pool'
  }
];

const INPERSON_METHODS: {
  key: PaymentMethod;
  label: string;
  icon: React.ReactNode;
  desc: string;
}[] = [
  {
    key: 'cage',
    label: 'Cage Withdrawal',
    icon: <AccountBalanceIcon style={{ fontSize: '1.2rem' }} />,
    desc: 'In-person at a club'
  }
];

const WithdrawView = () => {
  const {
    selectedPool,
    setSelectedPool,
    selectedMethod,
    setSelectedMethod,
    amount,
    setAmount,
    step,
    setStep,
    closeCashier,
    awaitCashierSession,
    isOpen
  } = useContext(CashierContext);
  const limits = useCashierLimits();
  const userBalance = useAppSelector((state) => state.user.playerBankAmount);
  const playerGUID = useAppSelector((state) => state.user.playerGUID);
  const contentRef = useRef<HTMLDivElement>(null);
  const [customAmount, setCustomAmount] = useState('');
  const [selectedClub, setSelectedClub] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');

  // Clear the transient withdraw amount when the cashier is exited (Unity
  // parity). `amount` lives in CashierContext and is reset by openCashier();
  // this local field otherwise survives (the panel stays mounted while hidden)
  // and reopening showed a stale amount with a context-reset "$0.00" Continue.
  useEffect(() => {
    if (!isOpen) setCustomAmount('');
  }, [isOpen]);
  const [p2pRecipient, setP2pRecipient] = useState('');
  const [p2pLookupLoading, setP2pLookupLoading] = useState(false);
  const [p2pLookupError, setP2pLookupError] = useState('');
  const [p2pTransferLoading, setP2pTransferLoading] = useState(false);
  const [p2pSearchResults, setP2pSearchResults] = useState<
    { GUID: string; displayName: string; email: string; phone: string }[]
  >([]);
  const [p2pResolvedPlayer, setP2pResolvedPlayer] = useState<{
    GUID: string;
    displayName: string;
    email: string;
    phone: string;
  } | null>(null);
  const { clubs } = useClubs();
  const { profile, fetchProfile, error: profileError } = usePlayerProfile('cashier-withdraw');
  const [profilePending, setProfilePending] = useState(true);
  const [profileRetry, setProfileRetry] = useState(0);
  const { runRisk, loading: riskLoading } = useRiskAssessment();
  const [showMissingWalletModal, setShowMissingWalletModal] = useState(false);

  // Local-only address-required state. Lives for one WithdrawView mount —
  // refetches every time the user lands on the Withdraw tab or reopens the
  // cashier, and is never shared with other components or persisted.
  // null = not yet known (notice/disable suppressed until we have an answer).
  const [addressRequired, setAddressRequired] = useState<boolean | null>(null);

  // Mirrors Unity CashiersController.IsIdVerified (CashiersController.cs:56-60)
  const idVerifiedOk = ['APPROVED', 'SUSPECTED'].includes(
    String(profile.idVerified || '').toUpperCase()
  );

  // The cashier stays mounted while hidden; only load its profile when opened.
  useEffect(() => {
    if (!isOpen) return;
    const opening = new AbortController();
    setProfilePending(true);
    const loadProfile = async () => {
      await fetchProfile({ signal: opening.signal });
      if (!opening.signal.aborted) setProfilePending(false);
    };
    void loadProfile();
    return () => {
      opening.abort();
      setProfilePending(true);
    };
  }, [isOpen, fetchProfile, profileRetry]);

  useEffect(() => {
    let cancelled = false;
    checkAddressRequired().then((required) => {
      if (!cancelled) setAddressRequired(required);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // The wired-up listener lives in SaloonHeader / LobbyHeader; it listens
  // for `'open-profile-panel'` and reads `event.detail.field`. Earlier
  // versions of these two helpers dispatched `'hijack:open-profile-panel'`
  // with `focusField`, which has no listener anywhere — clicks called
  // closeCashier() and silently no-op'd.
  const openAccountSettingsForWallet = () => {
    closeCashier();
    window.dispatchEvent(
      new CustomEvent('open-profile-panel', {
        detail: { field: 'withdrawalWalletAddress' }
      })
    );
  };

  const [withdrawError, setWithdrawError] = useState('');
  const [gateLoading, setGateLoading] = useState(false);
  const openProfilePanel = (field: 'streetAddress' | 'idVerification') => {
    closeCashier();
    window.dispatchEvent(
      new CustomEvent('open-profile-panel', { detail: { field } })
    );
  };

  useEffect(() => {
    setShowMissingWalletModal(
      isOpen && !profilePending && !profileError &&
      selectedMethod === 'crypto' && !profile.withdrawalWalletAddress
    );
  }, [isOpen, profilePending, profileError, selectedMethod, profile.withdrawalWalletAddress]);

  const goToProfileForWallet = () => {
    setShowMissingWalletModal(false);
    setSelectedMethod(null as any);
    closeCashier();
    window.dispatchEvent(
      new CustomEvent('open-profile-panel', { detail: { field: 'withdrawalWalletAddress' } })
    );
  };

  // Mirrors Unity CashiersMenuView's GO_TO_ACCOUNT_SETTINGS_BUTTON handler:
  // close the cashier and route to ProfilePanel with the streetAddress
  // field focused.
  const goToProfileForAddress = () => {
    closeCashier();
    window.dispatchEvent(
      new CustomEvent('open-profile-panel', { detail: { field: 'streetAddress' } })
    );
  };

  // KYC state (for crypto withdrawals)
  const [showKYC, setShowKYC] = useState(false);
  const [kycQuestions, setKycQuestions] = useState<any>(null);
  const [kycRequestId, setKycRequestId] = useState('');
  const [kycLoading, setKycLoading] = useState(false);
  // Marks the 'error' step as a terminal identity-check outcome (failed questionnaire
  // or lockdown). Unity's KycQuizView shows a dismiss-only AssessmentFailedView with a
  // "Go Back" button — never a "Try Again" that would re-attempt and trip the lockdown.
  const [kycTerminal, setKycTerminal] = useState(false);
  // Tracks where the KYC modal was triggered from so handleKycSubmit knows
  // what to do on pass: 'preMfa' → advance to MFA (Unity flow);
  // 'postInitiate' → retry the withdrawal call (legacy fallback).
  const [kycPostFlow, setKycPostFlow] = useState<'preMfa' | 'postInitiate'>(
    'postInitiate'
  );
  const processWithdrawal = async () => {
    setStep('processing');
    setWithdrawError('');

    if (selectedMethod === 'cage') {
      try {
        const base = SOCKET_SERVER_URL?.replace(/^wss?:/, 'https:').replace(/\/+$/, '') || '';
        const jwt = getDescopeToken();

        const res = await fetch(`${base}/api/clubWithrawal`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`
          },
          body: JSON.stringify({
            player_id: playerGUID,
            category: 'cage',
            type: 'withdrawal',
            currency: 'USD',
            amount: String(amount),
            affiliateId: selectedClub,
            token: jwt,
            jwt: jwt
          })
        });

        const json = await res.json();
        const txData = json?.data?.data || json?.data;
        if ((json?.success || json?.data?.rc === '0') && txData?.code) {
          limits.refresh();
          setConfirmationCode(String(txData.code));
          setStep('success');
        } else {
          console.error('[Withdraw] Unexpected response:', json);
          setWithdrawError(json?.data?.message || json?.message || 'Withdrawal failed.');
          setStep('error');
        }
      } catch (e) {
        console.error('[Withdraw] Cage withdrawal failed:', errorToLogObject(e));
        setWithdrawError('Withdrawal failed. Please try again.');
        setStep('error');
      }
    } else if (selectedMethod === 'crypto') {
      // Defense-in-depth — gate chain ran before MFA, but state may have
      // changed. Mirrors Unity's pre-submit guards in CryptoWithdrawView.cs.
      const walletAddress = profile.withdrawalWalletAddress;
      if (!walletAddress) {
        setWithdrawError(WITHDRAWAL_WALLET_ADDRESS_EMPTY_MESSAGE2);
        setStep('error');
        return;
      }
      if (amount < 20) {
        setWithdrawError(CRYPTO_MINIMUM_MESSAGE);
        setStep('error');
        return;
      }
      if (await checkAddressRequired()) {
        setWithdrawError(ADDRESS_REQUIRED_TRANSACTION_MESSAGE);
        setStep('error');
        return;
      }
      if (!idVerifiedOk) {
        setWithdrawError(NO_VERIFICATION_MESSAGE);
        setStep('error');
        return;
      }
      await processCryptoWithdrawal();
    } else if (selectedMethod === 'p2p') {
      await processP2PTransfer();
    }
  };

  // KYC functions for crypto withdrawal — `getBackofficeUrl` imported from utils/urls.

  // Mirrors Unity LexisNexisModel.GetKYCQuestion (LexisNexisModel.cs:237-306):
  // up to 3 attempts on connection error, 1.5s × attempt backoff.
  const fetchKycQuestions = async () => {
    setKycLoading(true);
    setKycTerminal(false);
    const maxRetries = 3;
    const retryDelayMs = 1500;
    try {
      const jwt = getDescopeToken();
      let lastError: unknown = null;
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const res = await fetch(
            `${getBackofficeUrl()}/api/risk/kyc/questions?playerGuid=${playerGUID}`,
            {
              headers: { Authorization: `Bearer ${jwt}` }
            }
          );

          // Any non-success is a terminal identity-check outcome. Mirror Unity's
          // GetKYCQuestion → OnKycQuestionEvent exactly: prefer the backend `message`,
          // fall back to `error`, then ERROR_MESSAGE (Unity's precedence/fallback).
          // HTTP errors aren't network failures, so we don't retry them (return, like Unity).
          if (!res.ok) {
            const data = await safeReadJson(res);
            setWithdrawError(pickApiErrorMessage(data, ERROR_MESSAGE));
            setKycTerminal(true);
            setStep('error');
            return;
          }

          const data = await res.json();
          setKycQuestions(data.questions);
          setKycRequestId(data.requestId);
          setShowKYC(true);
          setStep('select');
          return;
        } catch (e) {
          // Network/connection error — retry per Unity behavior
          lastError = e;
          if (attempt < maxRetries) {
            await new Promise((r) => setTimeout(r, retryDelayMs * attempt));
            continue;
          }
        }
      }
      console.error('[Withdraw] KYC fetch failed after retries:', lastError);
      setWithdrawError(CONNECTION_ERROR_MESSAGE);
      setKycTerminal(true);
      setStep('error');
    } finally {
      setKycLoading(false);
    }
  };

  const handleKycSubmit = async (answers: any) => {
    // A failed questionnaire is terminal — matching Unity's KycQuizView, which swaps
    // the quiz for a terminal AssessmentFailedView with no resubmit path. Close the
    // modal and route to the terminal 'error' step so the same requestId can't be
    // re-submitted; retrying means restarting the flow for a fresh requestId.
    const terminateKyc = (message: string) => {
      setShowKYC(false);
      setKycTerminal(true);
      setStep('error');
      setWithdrawError(message);
    };

    setKycLoading(true);
    try {
      const jwt = getDescopeToken();
      const res = await fetch(`${getBackofficeUrl()}/api/risk/kyc/questions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
        body: JSON.stringify({ playerGuid: playerGUID, requestId: kycRequestId, answers })
      });

      const data = await safeReadJson(res);

      // Distinguish a transport/server error from a successful answer-rejection,
      // matching Unity's LexisNexisModel.PostKYCAnswerToQuestion: HTTP non-success
      // routes through onFailure with the backend-supplied message, while a 200
      // with reviewStatus != PASS is a fixed "verification failed" UX state.
      if (!res.ok) {
        console.error('[Withdraw] KYC submit failed:', res.status, data);
        terminateKyc(pickApiErrorMessage(data, ERROR_MESSAGE));
        return;
      }

      const reviewStatus =
        data && typeof data === 'object' ? (data as any).reviewStatus : undefined;

      if (reviewStatus === 'pass') {
        setShowKYC(false);
        if (kycPostFlow === 'preMfa') {
          // KYC was triggered by the pre-MFA risk check — advance to MFA per Unity flow
          setKycPostFlow('postInitiate');
          setStep('mfa');
        } else {
          // Legacy/fallback path: KYC surfaced from the withdrawal initiate response —
          // retry the withdrawal call.
          await processCryptoWithdrawal();
        }
      } else {
        terminateKyc(IDENTITY_CHECK_FAILED_MESSAGE);
      }
    } catch (e) {
      console.error('[Withdraw] KYC submit network error:', errorToLogObject(e));
      terminateKyc(ERROR_MESSAGE);
    } finally {
      setKycLoading(false);
    }
  };

  // Pre-MFA gate chain for crypto withdrawals.
  // Mirrors the flow in CryptoWithdrawView.cs: address check → idVerified →
  // PostRisk (TMX session) → KYC if required → MFA. Branch logic on the
  // /api/risk response uses kycRequired only (decision/reasons/tier ignored,
  // matching Unity's LexisNexisModel.PostRisk).
  const runCryptoGateChain = async (): Promise<void> => {
    setWithdrawError('');
    setGateLoading(true);
    try {
      // Gate 1: mailing address (server-driven). Fresh fetch every click —
      // no shared state, no cache.
      if (await checkAddressRequired()) {
        setWithdrawError(ADDRESS_REQUIRED_TRANSACTION_MESSAGE);
        setStep('error');
        return;
      }

      // Gate 2: idVerified pre-check (matches CashiersController.IsIdVerified)
      if (!idVerifiedOk) {
        setWithdrawError(NO_VERIFICATION_MESSAGE);
        setStep('error');
        return;
      }

      // Gate 3: cached TMX session → /api/risk → KYC if required.
      // The session was profiled when the Withdraw tab activated; reuse it.
      // awaitCashierSession() waits for any in-flight profiling (or 30s
      // fail-open) so the submit never fires with a half-baked session.
      const sessionId = await awaitCashierSession();
      const risk = await runRisk({
        sessionId,
        eventType: 'payment',
        playerGuid: playerGUID,
        amount,
        pageId: TMXPageData.Withdrawal
      });

      if (!risk.ok) {
        // Hard-stop on risk failure (Unity behavior — LexisNexisModel.cs:133-146)
        setWithdrawError(risk.message);
        setStep('error');
        return;
      }

      if (risk.kycRequired) {
        setKycPostFlow('preMfa');
        await fetchKycQuestions();
        return;
      }

      setStep('mfa');
    } finally {
      setGateLoading(false);
    }
  };

  // Pre-MFA gate chain for cage / p2p flows. Mirrors CashierWithdrawView.cs:232
  // (cage) and CashiersTransferView.cs:449 (transfer) — same PostRisk → KYC
  // branching as crypto.
  //
  // Unity gates these flows on address-required at the menu-tap level
  // (CashiersMenuView.ButtonToWithrawTapped / ButtonToTransferTapped); the
  // gate chain is the closest pre-transaction equivalent in React.
  //
  // Note: Unity calls `PostRisk(amount, GameConstants.PAYMENT, ...)` for ALL
  // cashier flows including p2p transfer — the `'transfer'` enum value exists
  // on the backend but Unity never sends it. Match that exactly.
  const runCashierGateChain = async (
    eventPageId: number
  ): Promise<void> => {
    setWithdrawError('');
    setGateLoading(true);
    try {
      // Gate 1: mailing address.
      if (await checkAddressRequired()) {
        setWithdrawError(ADDRESS_REQUIRED_TRANSACTION_MESSAGE);
        setStep('error');
        return;
      }

      // Session was profiled when the Withdraw tab/method activated; reuse it.
      // (eventPageId still drives runRisk's pageId for cage vs p2p/Transfer.)
      const sessionId = await awaitCashierSession();
      const risk = await runRisk({
        sessionId,
        eventType: 'payment',
        playerGuid: playerGUID,
        amount,
        pageId: eventPageId
      });

      if (!risk.ok) {
        setWithdrawError(risk.message);
        setStep('error');
        return;
      }

      if (risk.kycRequired) {
        setKycPostFlow('preMfa');
        await fetchKycQuestions();
        return;
      }

      setStep('mfa');
    } finally {
      setGateLoading(false);
    }
  };

  // Extracted crypto withdrawal logic so KYC can call it after passing
  const processCryptoWithdrawal = async () => {
    setStep('processing');
    const walletAddress = profile.withdrawalWalletAddress;

    try {
      const jwt = getDescopeToken();

      const res = await fetch(`${getBackofficeUrl()}/api/v2/forumpay/withdrawal/initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`
        },
        body: JSON.stringify({
          guid: playerGUID,
          amount: String(amount),
          recipient_address: walletAddress,
          currency: 'BTC',
          jwt: jwt
        })
      });

      const json = await res.json();

      // Check if KYC is required
      if (json?.kycRequired === true) {
        console.log('[Withdraw] KYC required for crypto withdrawal');
        await fetchKycQuestions();
        return;
      }

      if (json?.success) {
        limits.refresh();
        setStep('success');
      } else {
        setWithdrawError(json?.message || json?.data?.message || 'Crypto withdrawal failed.');
        setStep('error');
      }
    } catch (e: any) {
      console.error('[Withdraw] Crypto withdrawal failed:', errorToLogObject(e));
      // Check error response for KYC requirement
      try {
        const errData = await e?.response?.json?.();
        if (errData?.kycRequired === true) {
          await fetchKycQuestions();
          return;
        }
      } catch {
        /* noop */
      }
      setWithdrawError('Crypto withdrawal failed. Please try again.');
      setStep('error');
    }
  };

  const lookupP2PRecipient = async () => {
    const recipient = p2pRecipient.trim();
    if (!recipient) {
      setP2pLookupError('Enter an email, display name, or phone number.');
      setP2pSearchResults([]);
      setP2pResolvedPlayer(null);
      return;
    }

    setP2pLookupLoading(true);
    setP2pLookupError('');
    setP2pSearchResults([]);
    setP2pResolvedPlayer(null);

    try {
      const jwt = getDescopeToken();
      const res = await fetch(`${getBackofficeUrl()}/api/v2/players/transfer/player`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`
        },
        body: JSON.stringify({
          jwt,
          player: recipient
        })
      });

      const json = await res.json();
      const rawPlayers = Array.isArray(json?.data?.values)
        ? json.data.values
        : Array.isArray(json?.data)
        ? json.data
        : [];
      const players = rawPlayers.filter((player: any) => player?.GUID);

      if (res.ok && players.length > 0) {
        setP2pSearchResults(players);
        return;
      }

      setP2pLookupError(json?.message || 'Player not found.');
    } catch (e) {
      console.error('[Withdraw] P2P recipient lookup failed:', errorToLogObject(e));
      setP2pLookupError('Could not find that player. Please try again.');
    } finally {
      setP2pLookupLoading(false);
    }
  };

  const processP2PTransfer = async () => {
    if (!p2pResolvedPlayer?.GUID) {
      setWithdrawError('Select a valid player before sending chips.');
      setStep('error');
      return;
    }

    setP2pTransferLoading(true);

    try {
      const jwt = getDescopeToken();
      // Server derives senderGUID from the verified Descope token in the
      // Authorization header. We no longer pass senderGUID/token/jwt in the body —
      // they're either redundant (token/jwt) or actively rejected (senderGUID).
      const res = await fetch(`${getBackofficeUrl()}/api/players/give-chips`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`
        },
        body: JSON.stringify({
          receiverGUID: p2pResolvedPlayer.GUID,
          value: amount,
          name: 'winpot',
          type: 'Transfer'
        })
      });

      const json = await res.json();

      if (res.ok && json?.response?.status !== 1) {
        limits.refresh();
        setStep('success');
        return;
      }

      setWithdrawError(json?.message || 'Player transfer failed.');
      setStep('error');
    } catch (e) {
      console.error('[Withdraw] P2P transfer failed:', errorToLogObject(e));
      setWithdrawError('Player transfer failed. Please try again.');
      setStep('error');
    } finally {
      setP2pTransferLoading(false);
    }
  };

  const pool = selectedPool === 'digital' ? limits.withdrawal.digital : limits.withdrawal.inperson;
  const methods = selectedPool === 'digital' ? DIGITAL_METHODS : INPERSON_METHODS;
  // Crypto button ceiling mirrors Unity (CryptoWithdrawView.cs:108 +
  // CashiersController.HandleStringValueChanged:402): the Withdraw button gates on
  // the GROSS daily withdrawal limit, not the net-of-today remaining or the wallet
  // balance. Used-today overflow and insufficient balance are enforced at submit /
  // by the backoffice — matching Unity's ButtonSubmitCryptoWithdraw, which surfaces
  // those as messages rather than disabling the button. Cage/p2p keep the
  // remaining-and-balance ceiling.
  const maxAmount =
    selectedMethod === 'crypto'
      ? limits.withdrawal.methods.crypto.dailyLimit
      : // Player transfer has no configured tiered limit — the wallet balance is
      // the only ceiling (Unity parity).
      selectedMethod === 'p2p'
      ? userBalance
      : selectedPool
      ? Math.min(pool.remaining, userBalance)
      : 0;
  const handsRequirementMessage =
    limits.withdrawalHandsPlayedMin > 0
      ? `You must play at least ${limits.withdrawalHandsPlayedMin} hands before making a player transfer or withdrawal. You have played ${limits.handsPlayed} of ${limits.withdrawalHandsPlayedMin}.`
      : '';

  // Single source of truth for the Withdraw button's disabled state. Ordered
  // list — first active gate wins, mirroring the original if/else-if precedence.
  // Both the render (below) and the debug log read from this, so they can't drift.
  const minAmount = selectedMethod === 'crypto' ? 20 : 10;
  const disableGates: Array<[key: string, active: boolean, reason: string]> = [
    ['crypto:gateLoading', selectedMethod === 'crypto' && gateLoading, IDENTITY_PROCESS_CHECKING],
    ['crypto:riskLoading', selectedMethod === 'crypto' && riskLoading, IDENTITY_PROCESS_CHECKING],
    ['noMethod', !selectedMethod, 'Select a withdrawal method.'],
    [
      'handsRequirement',
      !limits.withdrawalHandsRequirementMet,
      handsRequirementMessage || 'Hands-played requirement not met.'
    ],
    ['amountZero', amount <= 0, 'Enter an amount.'],
    ['crypto:belowMin', selectedMethod === 'crypto' && amount < minAmount, CRYPTO_MINIMUM_MESSAGE],
    ['belowMin', amount < minAmount, WITHDRAW_MINIMUM],
    ['aboveMax', amount > maxAmount, formatUnityString(EXCEEDED_MAXIMUM_WITHDRAW, String(maxAmount))],
    ['cage:noClub', selectedMethod === 'cage' && !selectedClub, CLUB_NOT_SELECTED],
    [
      'crypto:noWallet',
      selectedMethod === 'crypto' && !profile.withdrawalWalletAddress,
      WITHDRAWAL_WALLET_ADDRESS_EMPTY_MESSAGE1
    ],
    [
      'crypto:addressRequired',
      selectedMethod === 'crypto' && addressRequired === true,
      ADDRESS_REQUIRED_TRANSACTION_MESSAGE
    ],
    ['crypto:notIdVerified', selectedMethod === 'crypto' && !idVerifiedOk, NO_VERIFICATION_MESSAGE],
    ['p2p:noRecipient', selectedMethod === 'p2p' && !p2pResolvedPlayer?.GUID, 'Select a recipient.'],
    ['p2pSending', p2pTransferLoading, 'Sending…']
  ];
  const firstActiveGate = disableGates.find(([, active]) => active) ?? null;
  const disabledReason = firstActiveGate ? firstActiveGate[2] : null;

  useEffect(() => {
    if (contentRef.current) {
      gsap.fromTo(
        contentRef.current,
        { opacity: 0, y: 10 },
        { opacity: 1, y: 0, duration: 0.3, ease: 'power2.out' }
      );
    }
  }, [step, selectedPool]);

  useEffect(() => {
    setP2pLookupError('');
    setP2pSearchResults([]);
    setP2pResolvedPlayer(null);
  }, [selectedMethod]);

  if (showKYC) {
    return (
      <KYCModal
        open={showKYC}
        questions={kycQuestions}
        onSubmit={handleKycSubmit}
        onClose={() => {
          setShowKYC(false);
          setStep('error');
          setWithdrawError('Identity verification is required for crypto withdrawals.');
        }}
        loading={kycLoading}
      />
    );
  }

  if (step === 'mfa') return <MFAView onVerified={processWithdrawal} phone={profile.phone} />;

  if (step === 'processing') {
    return (
      <div style={centeredStyle}>
        <div style={spinnerStyle} />
        <span style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
          Processing {currencyFormatter(amount)} withdrawal...
        </span>
      </div>
    );
  }

  if (step === 'error') {
    // Unity parity: a failed/locked-out identity check is terminal (KycQuizView's
    // AssessmentFailedView). It offers only a dismiss ("Go Back") — never a "Try
    // Again", which would re-attempt and walk the player straight into the lockdown.
    if (kycTerminal) {
      return (
        <div style={centeredStyle}>
          <span style={{ fontSize: '2rem' }}>⚠️</span>
          <span style={{ ...successLabel, color: '#c0392b' }}>Identity Check</span>
          <span style={successSub}>{withdrawError || IDENTITY_CHECK_FAILED_MESSAGE}</span>
          <button
            onClick={() => {
              setKycTerminal(false);
              setStep('select');
            }}
            style={{ ...continueBtn, marginTop: '12px', maxWidth: '200px' }}>
            Go Back
          </button>
        </div>
      );
    }

    const isAddressMissing = withdrawError === ADDRESS_REQUIRED_TRANSACTION_MESSAGE;
    return (
      <div style={centeredStyle}>
        <span style={{ fontSize: '2rem' }}>⚠️</span>
        <span style={{ ...successLabel, color: '#c0392b' }}>Withdrawal Failed</span>
        <span style={successSub}>{withdrawError || 'Something went wrong. Please try again.'}</span>
        {isAddressMissing && (
          <button
            onClick={goToProfileForAddress}
            style={{ ...continueBtn, marginTop: '12px', maxWidth: '240px' }}>
            {GO_TO_ACCOUNT_SETTINGS_BUTTON}
          </button>
        )}
        <button
          onClick={() => setStep('select')}
          style={{ ...continueBtn, marginTop: '12px', maxWidth: '200px' }}>
          Try Again
        </button>
      </div>
    );
  }

  if (step === 'success') {
    const isCage = selectedMethod === 'cage';
    // "Back to Cashier" — reset the withdraw flow to the method-selection menu
    // so the player stays in the Cashier (the header back arrow exits to the
    // Lobby). Matches the deposit success screen.
    const returnToCashierMenu = () => {
      setSelectedMethod(null as any);
      setSelectedPool(null as any);
      setAmount(0);
      setStep('select');
      setCustomAmount('');
      setSelectedClub('');
      setConfirmationCode('');
    };
    return (
      <div style={centeredStyle}>
        <CheckCircleIcon style={{ fontSize: '3rem', color: '#4CAF50' }} />
        <span style={successAmount}>{currencyFormatter(amount)}</span>
        <span style={successLabel}>{isCage ? 'Withdrawal Requested' : 'Withdrawal Submitted'}</span>

        {isCage && confirmationCode ? (
          <>
            <span style={successSub}>Present this code at the cage to collect your funds.</span>
            <div style={codeDisplayRow}>
              {confirmationCode.split('').map((char, i) => (
                <div key={i} style={codeDisplayDigit}>
                  {char}
                </div>
              ))}
            </div>
            <span style={successSub}>A copy has also been sent to your email.</span>
          </>
        ) : (
          <span style={successSub}>
            {selectedMethod === 'p2p'
              ? 'Your chips were sent successfully.'
              : "You'll receive your funds shortly."}
          </span>
        )}

        <button onClick={returnToCashierMenu} style={successDoneBtn}>
          Back to Cashier
        </button>
      </div>
    );
  }

  if (step === 'select' && (profilePending || profileError)) {
    return (
      <div style={centeredStyle}>
        {profilePending ? (
          <span role='status' style={profileStatusText}>Loading cashier profile...</span>
        ) : (
          <>
            <span role='alert' style={profileStatusText}>Your profile is temporarily unavailable. Please try again.</span>
            <button style={successDoneBtn} onClick={() => setProfileRetry((attempt) => attempt + 1)}>
              Retry
            </button>
          </>
        )}
      </div>
    );
  }

  if (limits.loading && step === 'select') {
    return (
      <div style={centeredStyle}>
        <div style={spinnerStyle} />
        <span style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
          Loading cashier limits...
        </span>
      </div>
    );
  }

  return (
    <>
      <div ref={contentRef} style={containerStyle}>
      {!selectedPool && (
        <>
          <div style={sectionLabel}>Choose withdrawal type</div>
          {!limits.withdrawalHandsRequirementMet && handsRequirementMessage && (
            <div style={warningBanner}>{handsRequirementMessage}</div>
          )}
          <div style={poolRow}>
            {(['digital', 'inperson'] as const).map((p) => {
              const lim = limits.withdrawal[p];
              return (
                <div
                  key={p}
                  role="button"
                  aria-disabled={!limits.withdrawalHandsRequirementMet}
                  onClick={() => {
                    if (!limits.withdrawalHandsRequirementMet) return;
                    setSelectedPool(p);
                  }}
                  style={{
                    ...poolCard,
                    ...(!limits.withdrawalHandsRequirementMet ? methodCardDisabled : {})
                  }}>
                  {p === 'digital' ? (
                    <DevicesIcon style={{ fontSize: '1.5rem', color: '#63b3ed' }} />
                  ) : (
                    <StorefrontIcon style={{ fontSize: '1.5rem', color: '#E98C26' }} />
                  )}
                  <span style={poolLabel}>{p === 'digital' ? 'Digital' : 'In-Person'}</span>
                  <span style={poolSub}>
                    {p === 'digital' ? 'Crypto, Player Transfer' : 'Cage at club'}
                  </span>
                  <div style={poolBarTrack}>
                    <div style={{ ...poolBarFill, width: `${lim.usagePercent}%` }} />
                  </div>
                  <span style={poolRemaining}>{currencyFormatter(lim.remaining)} left</span>
                </div>
              );
            })}
          </div>
          <div style={balanceNote}>Available to withdraw: {currencyFormatter(userBalance)}</div>
          <TierExplainer
            tierInfo={limits.tierInfo}
            handsPlayed={limits.handsPlayed}
            idVerified={limits.idVerified}
          />
        </>
      )}

      {selectedPool && !selectedMethod && (
        <>
          <button
            onClick={() => {
              setSelectedPool(null as any);
              setSelectedMethod(null as any);
              setAmount(0);
            }}
            style={backBtn}>
            ← Back
          </button>

          <div style={headerRow}>
            <span style={headerTitle}>
              {selectedPool === 'digital' ? 'Digital' : 'In-Person'} Withdrawal
            </span>
            <span style={limitText}>
              {selectedPool === 'digital'
                ? 'Shared online withdrawal pool for crypto and player transfer'
                : 'In-person withdrawal pool'}
            </span>
          </div>

          {!limits.withdrawalHandsRequirementMet && handsRequirementMessage && (
            <div style={warningBanner}>{handsRequirementMessage}</div>
          )}

          <div style={limitBarWrap}>
            <div style={limitTrack}>
              <div style={{ ...limitFill, width: `${pool.usagePercent}%` }} />
            </div>
            <span style={limitProgressText}>
              {currencyFormatter(pool.used)} / {currencyFormatter(pool.dailyLimit)}
            </span>
          </div>

          <div style={sectionLabel}>Method</div>
          {methods.map((m) => (
            <button
              key={m.key}
              disabled={!limits.withdrawalHandsRequirementMet}
              onClick={() => setSelectedMethod(m.key)}
              style={{
                ...methodCard,
                ...(selectedMethod === m.key ? methodCardActive : {}),
                ...(!limits.withdrawalHandsRequirementMet ? methodCardDisabled : {})
              }}>
              <div style={methodIcon}>{m.icon}</div>
              <div style={methodInfo}>
                <span style={methodLabel}>{m.label}</span>
                <span style={methodDesc}>{m.desc}</span>
              </div>
              {selectedMethod === m.key && (
                <CheckCircleIcon style={{ fontSize: '1rem', color: '#E98C26' }} />
              )}
            </button>
          ))}
        </>
      )}

      {selectedPool && selectedMethod && (
        <>
          <button
            onClick={() => {
              setSelectedMethod(null as any);
              setAmount(0);
              setCustomAmount('');
            }}
            style={backBtn}>
            ← Back
          </button>

          <div style={headerRow}>
            <span style={headerTitle}>
              {selectedMethod === 'p2p'
                ? 'Player Transfer'
                : `${selectedPool === 'digital' ? 'Digital' : 'In-Person'} Withdrawal`}
            </span>
            <span style={limitText}>
              {selectedMethod === 'p2p'
                ? 'No daily transfer limit'
                : selectedPool === 'digital'
                ? 'Daily crypto withdrawal limit'
                : 'In-person withdrawal pool'}
            </span>
          </div>

          {!limits.withdrawalHandsRequirementMet && handsRequirementMessage && (
            <div style={warningBanner}>{handsRequirementMessage}</div>
          )}

          {/* Player transfer has no configured tiered limit → no limit bar. */}
          {selectedMethod !== 'p2p' && (
            <div style={limitBarWrap}>
              <div style={limitTrack}>
                <div style={{ ...limitFill, width: `${pool.usagePercent}%` }} />
              </div>
              <span style={limitProgressText}>
                {currencyFormatter(pool.used)} / {currencyFormatter(pool.dailyLimit)}
              </span>
            </div>
          )}

          <div style={sectionLabel}>Amount</div>
          <div style={amountRow}>
            <span style={dollar}>$</span>
            <input
              type='text'
              inputMode='decimal'
              placeholder='0.00'
              value={customAmount}
              onChange={(e) => {
                const cleaned = sanitizeCurrencyAmount(e.target.value);
                setCustomAmount(cleaned);
                setAmount(parseCurrencyAmount(cleaned));
              }}
              style={amountInput}
            />
          </div>
          {amount > maxAmount && maxAmount > 0 && (
            <div style={errorText}>Max withdrawable: {currencyFormatter(maxAmount)}</div>
          )}

          {/* Club selector for cage withdrawals */}
          {selectedMethod === 'cage' && (
            <>
              <div style={{ ...sectionLabel, marginTop: '8px' }}>Club Location</div>
              <select
                value={selectedClub}
                onChange={(e) => setSelectedClub(e.target.value)}
                style={selectInput}>
                <option value=''>Select a club...</option>
                {clubs.map((club) => (
                  <option key={club.key} value={club.locationId}>
                    {club.clubName}
                  </option>
                ))}
              </select>
            </>
          )}

          {/* Crypto: wallet address is managed in Account Settings (read-only here) */}
          {selectedMethod === 'crypto' && (
            <>
              <div style={{ ...sectionLabel, marginTop: '8px' }}>Bitcoin Withdrawal Address</div>
              {profile.withdrawalWalletAddress ? (
                <div style={walletBox}>
                  <CurrencyBitcoinIcon
                    style={{ fontSize: '1rem', color: '#E98C26', flexShrink: 0 }}
                  />
                  <span style={walletAddrText}>{profile.withdrawalWalletAddress}</span>
                  <button onClick={openAccountSettingsForWallet} style={editBtn}>
                    Change
                  </button>
                </div>
              ) : (
                <div style={walletMissingBox}>
                  <span style={walletMissingText}>
                    No Bitcoin withdrawal address set. Add one in Account Settings to continue.
                  </span>
                  <button onClick={openAccountSettingsForWallet} style={walletMissingBtn}>
                    Open Account Settings
                  </button>
                </div>
              )}
              {addressRequired === true && (
                <div style={walletMissingBox}>
                  <span style={walletMissingText}>
                    Please update your mailing address before proceeding with the transaction
                  </span>
                  <button
                    onClick={() => openProfilePanel('streetAddress')}
                    style={walletMissingBtn}>
                    Open Account Settings
                  </button>
                </div>
              )}
              {!idVerifiedOk && (
                <div style={walletMissingBox}>
                  <span style={walletMissingText}>
                    Identity verification required to withdraw crypto.
                  </span>
                  <button
                    onClick={() => openProfilePanel('idVerification')}
                    style={walletMissingBtn}>
                    Open Account Settings
                  </button>
                </div>
              )}
              {amount > 0 && amount < 20 && (
                <div style={minAmountNote}>Minimum crypto withdrawal is $20.</div>
              )}
            </>
          )}

          {selectedMethod === 'p2p' && (
            <>
              <div style={{ ...sectionLabel, marginTop: '8px' }}>Recipient</div>
              <div style={walletEditBox}>
                <input
                  type='text'
                  placeholder='Email, display name, or phone'
                  value={p2pRecipient}
                  onChange={(e) => {
                    setP2pRecipient(e.target.value);
                    setP2pLookupError('');
                    setP2pSearchResults([]);
                    setP2pResolvedPlayer(null);
                  }}
                  style={walletInput}
                />
                <button
                  type='button'
                  onClick={lookupP2PRecipient}
                  disabled={!p2pRecipient.trim() || p2pLookupLoading}
                  style={{
                    ...saveBtn,
                    opacity: !p2pRecipient.trim() || p2pLookupLoading ? 0.4 : 1
                  }}>
                  {p2pLookupLoading ? 'Looking up...' : 'Find Player'}
                </button>
                {p2pLookupError && <span style={saveErrorText}>{p2pLookupError}</span>}
              </div>

              {p2pSearchResults.length > 0 && (
                <div style={recipientList}>
                  {p2pSearchResults.map((player) => (
                    <button
                      key={player.GUID}
                      type='button'
                      onClick={() => setP2pResolvedPlayer(player)}
                      style={{
                        ...recipientCard,
                        ...(p2pResolvedPlayer?.GUID === player.GUID ? recipientCardActive : {})
                      }}>
                      <div style={methodInfo}>
                        <span style={methodLabel}>{player.displayName}</span>
                        <span style={methodDesc}>{player.email}</span>
                        <span style={methodDesc}>{player.phone}</span>
                      </div>
                      {p2pResolvedPlayer?.GUID === player.GUID && (
                        <CheckCircleIcon style={{ fontSize: '1rem', color: '#E98C26' }} />
                      )}
                    </button>
                  ))}
                </div>
              )}

              {p2pResolvedPlayer && (
                <div style={cryptoDisclaimer}>
                  Selected player: {p2pResolvedPlayer.displayName}. Click Send Chips to continue.
                </div>
              )}

              {!p2pResolvedPlayer && p2pSearchResults.length > 0 && (
                <div style={cryptoDisclaimer}>Select a player above to enable Send Chips.</div>
              )}
            </>
          )}

          {selectedMethod === 'crypto' && (
            <>
              {/* Estimated processing + review note — mirrors the Confirm
                  Withdrawal summary so the same guidance is visible while the
                  player is still entering the amount. */}
              <div style={estProcessingRow}>
                <span style={estProcessingLabel}>Estimated Processing</span>
                <span style={estProcessingValue}>Up to 24 Hrs</span>
              </div>
              <div style={cryptoDisclaimer}>
                A variable conversion fee of approximately 6% will be deducted from your final
                amount. Crypto withdrawals are reviewed by our team and typically take up to 24 hours
                to process. You'll receive an email when your withdrawal is approved.
              </div>
              <div style={cryptoReviewNote}>
                <strong style={cryptoReviewStrong}>Review your withdrawal:</strong> confirm the amount
                and wallet address. Crypto withdrawals can't be reversed. To change the address,
                update it in your account profile before continuing.
              </div>
            </>
          )}

          {selectedMethod === 'p2p' && (
            <div style={cryptoDisclaimer}>
              Player-to-player transfers use the same daily online withdrawal pool as crypto.
            </div>
          )}

          {(() => {
            // disabledReason / minAmount are computed once in the component body
            // (the `disableGates` list) so this render and the debug log stay in sync.
            const isDisabled = disabledReason !== null;
            const isLoading = gateLoading || riskLoading || p2pTransferLoading;

            const buttonLabel = (() => {
              if (gateLoading || riskLoading) return IDENTITY_PROCESS_CHECKING;
              if (p2pTransferLoading) return 'Sending…';
              const verb = selectedMethod === 'p2p' ? 'Send Chips' : 'Withdraw';
              return `${verb} · ${currencyFormatter(amount)}`;
            })();

            return (
              <>
                {isDisabled && !isLoading && (
                  <span style={disabledReasonText}>{disabledReason}</span>
                )}
                <button
                  onClick={() => {
                    // All cashier flows fire PostRisk before MFA per Unity:
                    //   crypto → CryptoWithdrawView.cs:359
                    //   cage   → CashierWithdrawView.cs:232
                    //   p2p    → CashiersTransferView.cs:449
                    if (selectedMethod === 'crypto') {
                      runCryptoGateChain();
                    } else if (selectedMethod === 'cage') {
                      runCashierGateChain(TMXPageData.Withdrawal);
                    } else if (selectedMethod === 'p2p') {
                      runCashierGateChain(TMXPageData.Transfer);
                    }
                  }}
                  disabled={isDisabled}
                  style={{
                    ...continueBtn,
                    opacity: isDisabled ? 0.4 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}>
                  {isLoading && <span style={btnSpinner} aria-hidden='true' />}
                  {buttonLabel}
                </button>
              </>
            );
          })()}
        </>
      )}
      </div>
      <MissingCryptoWalletModal
        open={showMissingWalletModal}
        onClose={() => setShowMissingWalletModal(false)}
        onGoToProfile={goToProfileForWallet}
      />
    </>
  );
};

export default WithdrawView;

const containerStyle: CSSProperties = { display: 'flex', flexDirection: 'column', gap: '12px' };
const sectionLabel: CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 700,
  color: 'rgba(255,255,255,0.4)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em'
};
const poolRow: CSSProperties = { display: 'flex', gap: '10px' };
const poolCard: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '6px',
  padding: '16px 12px',
  backgroundColor: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '14px',
  cursor: 'pointer'
};
const poolLabel: CSSProperties = { fontSize: '0.9rem', fontWeight: 700, color: '#fff' };
const poolSub: CSSProperties = { fontSize: '0.6rem', color: 'rgba(255,255,255,0.4)' };
const poolBarTrack: CSSProperties = {
  width: '100%',
  height: 4,
  backgroundColor: 'rgba(255,255,255,0.08)',
  borderRadius: 2,
  overflow: 'hidden',
  marginTop: '4px'
};
const poolBarFill: CSSProperties = {
  height: '100%',
  borderRadius: 2,
  backgroundColor: '#E98C26'
};
const poolRemaining: CSSProperties = { fontSize: '0.65rem', fontWeight: 600, color: '#4CAF50' };
const balanceNote: CSSProperties = {
  fontSize: '0.65rem',
  color: 'rgba(255,255,255,0.3)',
  textAlign: 'center'
};
const backBtn: CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'rgba(255,255,255,0.5)',
  fontSize: '0.75rem',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
  padding: 0,
  alignSelf: 'flex-start'
};
const headerRow: CSSProperties = { display: 'flex', flexDirection: 'column', gap: '4px' };
const headerTitle: CSSProperties = { fontSize: '1rem', fontWeight: 700, color: '#fff' };
const limitText: CSSProperties = { fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)' };
const limitBarWrap: CSSProperties = { display: 'flex', flexDirection: 'column', gap: '4px' };
const limitTrack: CSSProperties = {
  width: '100%',
  height: 5,
  backgroundColor: 'rgba(255,255,255,0.08)',
  borderRadius: 3,
  overflow: 'hidden'
};
const limitFill: CSSProperties = {
  height: '100%',
  borderRadius: 3,
  backgroundColor: '#E98C26'
};
const limitProgressText: CSSProperties = {
  fontSize: '0.65rem',
  color: 'rgba(255,255,255,0.5)'
};
const amountRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  backgroundColor: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '10px',
  padding: '0 12px'
};
const dollar: CSSProperties = { fontSize: '1rem', fontWeight: 600, color: 'rgba(255,255,255,0.4)' };
const amountInput: CSSProperties = {
  flex: 1,
  background: 'none',
  border: 'none',
  color: '#fff',
  fontSize: '1rem',
  fontWeight: 600,
  padding: '12px 0',
  outline: 'none',
  fontFamily: 'inherit'
};
const errorText: CSSProperties = { fontSize: '0.7rem', color: '#c0392b', fontWeight: 600 };
const selectInput: CSSProperties = {
  width: '100%',
  backgroundColor: '#1a2332',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '10px',
  color: '#fff',
  fontSize: '0.85rem',
  padding: '10px 12px',
  outline: 'none',
  fontFamily: 'inherit',
  boxSizing: 'border-box' as any,
  appearance: 'auto' as any
};
const methodCard: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  width: '100%',
  padding: '12px 14px',
  backgroundColor: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '12px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  marginBottom: '6px'
};
const methodCardActive: CSSProperties = {
  backgroundColor: 'rgba(233,140,38,0.1)',
  borderColor: 'rgba(233,140,38,0.3)'
};
const methodCardDisabled: CSSProperties = { opacity: 0.5, cursor: 'not-allowed' };
const methodIcon: CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: '10px',
  backgroundColor: 'rgba(255,255,255,0.06)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'rgba(255,255,255,0.7)',
  flexShrink: 0
};
const methodInfo: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'left'
};
const methodLabel: CSSProperties = { fontSize: '0.85rem', fontWeight: 600, color: '#fff' };
const methodDesc: CSSProperties = { fontSize: '0.65rem', color: 'rgba(255,255,255,0.4)' };
const continueBtn: CSSProperties = {
  width: '100%',
  padding: '14px',
  backgroundColor: '#E98C26',
  border: 'none',
  borderRadius: '12px',
  color: '#fff',
  fontSize: '0.95rem',
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit',
  marginTop: '8px'
};
const warningBanner: CSSProperties = {
  fontSize: '0.7rem',
  color: '#E98C26',
  fontWeight: 700,
  backgroundColor: 'rgba(233,140,38,0.08)',
  border: '1px solid rgba(233,140,38,0.2)',
  borderRadius: '10px',
  padding: '10px 12px',
  lineHeight: 1.4
};
const profileStatusText: CSSProperties = {
  fontSize: '0.9rem',
  color: 'rgba(255,255,255,0.7)',
  fontWeight: 600
};
const centeredStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
  padding: '40px 0',
  textAlign: 'center'
};
const successAmount: CSSProperties = { fontSize: '2rem', fontWeight: 800, color: '#4CAF50' };
const successLabel: CSSProperties = { fontSize: '1rem', fontWeight: 700, color: '#fff' };
const successSub: CSSProperties = { fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' };
const successDoneBtn: CSSProperties = {
  marginTop: '20px',
  padding: '12px 28px',
  background: 'rgba(233, 140, 38, 0.15)',
  border: '1px solid rgba(233, 140, 38, 0.4)',
  borderRadius: '10px',
  color: '#E98C26',
  fontSize: '0.9rem',
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit'
};
const spinnerStyle: CSSProperties = {
  width: 40,
  height: 40,
  border: '3px solid rgba(255,255,255,0.1)',
  borderTopColor: '#E98C26',
  borderRadius: '50%',
  animation: 'spin 0.8s linear infinite'
};
const codeDisplayRow: CSSProperties = {
  display: 'flex',
  gap: '6px',
  justifyContent: 'center',
  margin: '12px 0'
};
const codeDisplayDigit: CSSProperties = {
  width: 40,
  height: 48,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255,255,255,0.06)',
  border: '2px solid #E98C26',
  borderRadius: '8px',
  fontSize: '1.3rem',
  fontWeight: 800,
  color: '#E98C26',
  fontFamily: 'monospace'
};
const walletBox: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '12px',
  backgroundColor: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '10px'
};
const walletAddrText: CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 600,
  color: 'rgba(255,255,255,0.7)',
  fontFamily: 'monospace',
  wordBreak: 'break-all',
  lineHeight: 1.4
};
const editBtn: CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '6px',
  color: 'rgba(255,255,255,0.5)',
  fontSize: '0.6rem',
  fontWeight: 600,
  padding: '4px 10px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  flexShrink: 0
};
const walletEditBox: CSSProperties = { display: 'flex', flexDirection: 'column', gap: '8px' };
const walletMissingBox: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  padding: '14px',
  backgroundColor: 'rgba(233,140,38,0.08)',
  border: '1px solid rgba(233,140,38,0.25)',
  borderRadius: '12px'
};
const walletMissingText: CSSProperties = {
  fontSize: '0.75rem',
  color: '#E98C26',
  fontWeight: 600,
  lineHeight: 1.4
};
const walletMissingBtn: CSSProperties = {
  alignSelf: 'flex-start',
  padding: '8px 14px',
  backgroundColor: '#E98C26',
  border: 'none',
  borderRadius: '8px',
  color: '#fff',
  fontSize: '0.75rem',
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit'
};
const recipientList: CSSProperties = { display: 'flex', flexDirection: 'column', gap: '8px' };
const recipientCard: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  width: '100%',
  padding: '12px',
  backgroundColor: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '10px',
  cursor: 'pointer',
  fontFamily: 'inherit'
};
const recipientCardActive: CSSProperties = {
  backgroundColor: 'rgba(233,140,38,0.1)',
  borderColor: 'rgba(233,140,38,0.3)'
};
const walletInput: CSSProperties = {
  width: '100%',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '10px',
  color: '#fff',
  fontSize: '0.8rem',
  fontWeight: 600,
  padding: '10px 12px',
  outline: 'none',
  fontFamily: 'monospace',
  boxSizing: 'border-box' as any
};
const saveBtn: CSSProperties = {
  padding: '10px',
  backgroundColor: '#E98C26',
  border: 'none',
  borderRadius: '10px',
  color: '#fff',
  fontSize: '0.8rem',
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit'
};
const saveErrorText: CSSProperties = { fontSize: '0.65rem', color: '#e74c3c', fontWeight: 600 };
const minAmountNote: CSSProperties = { fontSize: '0.7rem', color: '#c0392b', fontWeight: 600 };
const cryptoDisclaimer: CSSProperties = {
  fontSize: '0.65rem',
  color: '#E98C26',
  lineHeight: 1.4,
  fontWeight: 600,
  marginTop: '4px'
};
// Estimated-processing summary row (label left, value right) shown on the crypto
// withdrawal entry screen — mirrors the Confirm Withdrawal summary.
const estProcessingRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  padding: '10px 12px',
  backgroundColor: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '10px',
  marginTop: '6px'
};
const estProcessingLabel: CSSProperties = {
  fontSize: '0.75rem',
  color: 'rgba(255,255,255,0.6)',
  fontWeight: 600
};
const estProcessingValue: CSSProperties = {
  fontSize: '0.75rem',
  color: '#fff',
  fontWeight: 700
};
const cryptoReviewNote: CSSProperties = {
  fontSize: '0.65rem',
  color: 'rgba(255,255,255,0.6)',
  lineHeight: 1.4,
  fontWeight: 500,
  marginTop: '4px'
};
const cryptoReviewStrong: CSSProperties = {
  color: 'rgba(255,255,255,0.85)',
  fontWeight: 700
};
const disabledReasonText: CSSProperties = {
  fontSize: '0.7rem',
  color: 'rgba(255,255,255,0.55)',
  fontWeight: 600,
  textAlign: 'center',
  lineHeight: 1.4,
  marginTop: '4px'
};
const btnSpinner: CSSProperties = {
  width: 14,
  height: 14,
  border: '2px solid rgba(255,255,255,0.3)',
  borderTopColor: '#fff',
  borderRadius: '50%',
  animation: 'spin 0.8s linear infinite',
  display: 'inline-block',
  flexShrink: 0
};
